import type {
  DomainEvent,
  RandomEventTileEntered,
  TerrainEntered,
  TileRevealed,
  TurnEnded,
} from "../../models/events.ts";
import {
  hasAutoEmitted,
  markAutoEmitted,
  type AutoNotificationGate,
  type LocalizedNotificationKind,
} from "../../models/notifications.ts";
import {
  DEFAULT_WORLD_BALANCE,
  type WorldBalance,
} from "../../models/config/worldBalance.ts";
import type { TileTerrain, WorldState } from "../../models/worldState.ts";
import { FIRE_EVENT_ID } from "../events/eventDefinitions.ts";
import { getFoodProduction } from "../economy/tileEconomy.ts";
import type { ReduceResult } from "../result.ts";
import {
  populationChangeKind,
  shouldPushFoodOverlayHint,
  shouldPushLandRecoveryHint,
} from "./autoNotificationPolicy.ts";
import { pushNotification } from "./notificationStore.ts";

const TERRAIN_NOTIFICATION_KINDS: Partial<Record<TileTerrain, LocalizedNotificationKind>> = {
  swamp: "swampEnter",
};

function pushKind(
  state: WorldState,
  localizedKind: LocalizedNotificationKind,
  events: DomainEvent[],
): WorldState {
  const pushed = pushNotification(state.notifications, { localizedKind });
  events.push({ type: "NotificationPushed", id: pushed.id, localizedKind });
  return { ...state, notifications: pushed.state };
}

function pushKinds(
  state: WorldState,
  localizedKinds: LocalizedNotificationKind[],
  events: DomainEvent[],
): WorldState {
  const pushed = pushNotification(state.notifications, { localizedKinds });
  events.push({
    type: "NotificationPushed",
    id: pushed.id,
    localizedKind: localizedKinds[0],
  });
  return { ...state, notifications: pushed.state };
}

function emitOnce(
  state: WorldState,
  gate: AutoNotificationGate,
  apply: (current: WorldState) => WorldState,
): WorldState {
  if (hasAutoEmitted(state.notifications, gate)) {
    return state;
  }
  const next = apply(state);
  return {
    ...next,
    notifications: markAutoEmitted(next.notifications, gate),
  };
}

function maybeFoodOverlay(
  state: WorldState,
  revealedTileCount: number,
  turn: number,
  events: DomainEvent[],
): WorldState {
  if (!shouldPushFoodOverlayHint({ revealedTileCount, turn })) {
    return state;
  }
  return emitOnce(state, "tutorialFoodOverlay", (current) =>
    pushKind(current, "tutorialFoodOverlay", events),
  );
}

function onTerrainEntered(
  state: WorldState,
  event: TerrainEntered,
  events: DomainEvent[],
): WorldState {
  const kind = TERRAIN_NOTIFICATION_KINDS[event.terrain];
  return kind ? pushKind(state, kind, events) : state;
}

function onPlayingStarted(state: WorldState, events: DomainEvent[]): WorldState {
  // Your first turn — after settle, before ending the first turn.
  let next = emitOnce(state, "tutorialTurns", (current) =>
    pushKind(current, "tutorialTurns", events),
  );
  next = emitOnce(next, "coreLoopTutorials", (current) =>
    pushKinds(
      current,
      ["tutorialStats", "tutorialWinGoal", "tutorialFoodEconomy"],
      events,
    ),
  );
  return emitOnce(next, "tutorialOneMove", (current) =>
    pushKind(current, "tutorialOneMove", events),
  );
}

function onTurnEnded(
  state: WorldState,
  event: TurnEnded,
  events: DomainEvent[],
  balance: WorldBalance,
): WorldState {
  let next = state;

  if (
    event.stayedOnTile &&
    event.foodProduction < getFoodProduction(event.terrain, balance)
  ) {
    next = emitOnce(next, "foodDepletion", (current) =>
      pushKind(current, "foodDepletion", events),
    );
  }

  const populationKind = populationChangeKind(
    event.populationBefore,
    event.populationAfter,
  );
  if (populationKind === "growth") {
    next = emitOnce(next, "tutorialGrowth", (current) =>
      pushKind(current, "tutorialGrowth", events),
    );
  } else if (populationKind === "famine") {
    next = emitOnce(next, "tutorialFamine", (current) =>
      pushKind(current, "tutorialFamine", events),
    );
  }

  if (
    shouldPushLandRecoveryHint({
      turn: event.turn,
      reactiveDepletionNotified: hasAutoEmitted(
        next.notifications,
        "foodDepletion",
      ),
    })
  ) {
    next = emitOnce(next, "tutorialLandRecovery", (current) =>
      pushKind(current, "tutorialLandRecovery", events),
    );
  }

  return maybeFoodOverlay(
    next,
    event.revealedTileCount,
    event.turn,
    events,
  );
}

function onTileRevealed(
  state: WorldState,
  event: TileRevealed,
  events: DomainEvent[],
): WorldState {
  return maybeFoodOverlay(
    state,
    event.revealedTileCount,
    event.turn,
    events,
  );
}

function onRandomEventTileEntered(
  state: WorldState,
  event: RandomEventTileEntered,
  events: DomainEvent[],
): WorldState {
  if (event.eventId !== FIRE_EVENT_ID) {
    return state;
  }
  if (!hasAutoEmitted(state.notifications, "tutorialRandomEvents")) {
    return emitOnce(state, "tutorialRandomEvents", (current) =>
      pushKinds(current, ["tutorialRandomEvents", "fireEnter"], events),
    );
  }
  return pushKind(state, "fireEnter", events);
}

/**
 * Deterministic notification pushes derived from domain events.
 * Same events ⇒ same WorldState.notifications on every host.
 */
export function applyAutoNotifications(
  result: ReduceResult,
  balance: WorldBalance = DEFAULT_WORLD_BALANCE,
): ReduceResult {
  if (result.events.some((event) => event.type === "CommandRejected")) {
    return result;
  }

  // Name and start-tile stay quiet; first tips land on PlayingStarted.
  if (result.state.phase !== "playing") {
    return result;
  }

  let state = result.state;
  const events: DomainEvent[] = [...result.events];

  for (const event of result.events) {
    switch (event.type) {
      case "TerrainEntered":
        state = onTerrainEntered(state, event, events);
        break;
      case "PlayingStarted":
        state = onPlayingStarted(state, events);
        break;
      case "TurnEnded":
        state = onTurnEnded(state, event, events, balance);
        break;
      case "TileRevealed":
        state = onTileRevealed(state, event, events);
        break;
      case "RandomEventTileEntered":
        state = onRandomEventTileEntered(state, event, events);
        break;
      default:
        break;
    }
  }

  return { state, events };
}
